import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { loadError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Trash2, ShieldCheck, User, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { SECTIONS, SectionPicker, PERMISSION_PRESETS } from "@/lib/sections";
import { LoginAudit } from "@/components/LoginAudit";
import { DeletionAudit } from "@/components/DeletionAudit";
import { Checkbox } from "@/components/ui/checkbox";

const empty = { name: "", email: "", password: "", role: "staff", sections: PERMISSION_PRESETS[0].sections, can_delete_bookings: false };

const DeleteToggle = ({ checked, onChange, testId }) => (
  <label className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer ${checked ? "border-red-400 bg-red-50 dark:bg-red-900/20" : "border-border"}`}>
    <Checkbox data-testid={testId} checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" />
    <span><span className="font-medium">Può eliminare prenotazioni</span><span className="block text-xs text-muted-foreground">Mostra il cestino in Prenotazioni e "Elimina gruppo". L'annullamento resta sempre disponibile.</span></span>
  </label>
);

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(loadError);

  const [permUser, setPermUser] = useState(null);
  const [permSections, setPermSections] = useState([]);
  const [permDelete, setPermDelete] = useState(false);
  const [savingPerm, setSavingPerm] = useState(false);
  const togglePerm = (k) => setPermSections((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k])); // eslint-disable-line no-unused-vars
  const savePerm = async () => {
    setSavingPerm(true);
    try {
      await api.patch(`/users/${permUser.id}/sections`, { sections: permSections, can_delete_bookings: permDelete });
      toast.success(`Permessi aggiornati per ${permUser.name}`);
      setPermUser(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSavingPerm(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Compila tutti i campi"); return; }
    try {
      await api.post("/auth/register", form);
      toast.success("Utente creato");
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Utente eliminato"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Accessi</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">Utenti & Ruoli</h1>
        </div>
        <Button data-testid="new-user-button" onClick={() => { setForm(empty); setOpen(true); }} className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full h-11 px-5 font-heading">
          <UserPlus className="w-4 h-4 mr-2" /> Nuovo Utente
        </Button>
      </div>

      <div className="grid gap-3" data-testid="users-list">
        {users.map((u) => (
          <Card key={u.id} className="p-4 border-border shadow-sm flex items-center justify-between gap-4 animate-fade-up">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-secondary grid place-items-center font-heading font-semibold">
                {(u.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div>
                <p className="font-medium">{u.name}</p>
                <p className="text-sm text-muted-foreground">{u.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={u.role === "admin" ? "bg-accent/15 text-accent border-0" : "bg-secondary text-secondary-foreground border-0"}>
                {u.role === "admin" ? <><ShieldCheck className="w-3.5 h-3.5 mr-1" />Direttore</> : <><User className="w-3.5 h-3.5 mr-1" />Receptionist</>}
              </Badge>
              {u.role !== "admin" && (
                <Button variant="outline" size="sm" data-testid={`permissions-${u.id}`} onClick={() => { setPermUser(u); setPermSections(u.sections || []); setPermDelete(!!u.can_delete_bookings); }}>
                  <SlidersHorizontal className="w-4 h-4 mr-1.5" />Sezioni ({(u.sections || []).length}/{SECTIONS.length})
                </Button>
              )}
              {u.id !== user.id && (
                <Button variant="ghost" size="sm" data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-5 border-border bg-secondary/30">
        <h3 className="font-heading font-semibold mb-2">Permessi per ruolo</h3>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div><p className="font-medium text-accent">Direttore</p><p className="text-muted-foreground">Accesso completo: camere, prenotazioni, calendario, fatture, report incassi e gestione utenti.</p></div>
          <div><p className="font-medium">Receptionist</p><p className="text-muted-foreground">Panoramica sempre visibile; le altre sezioni dipendono dai permessi assegnati (pulsante "Sezioni"). Nessun accesso a utenti e impostazioni.</p></div>
        </div>
      </Card>

      <LoginAudit />
      <DeletionAudit />

      <Dialog open={!!permUser} onOpenChange={(v) => !v && setPermUser(null)}>
        <DialogContent className="max-w-md" data-testid="permissions-dialog">
          {permUser && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-xl">Sezioni visibili — {permUser.name}</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">Panoramica è sempre visibile. Le sezioni non selezionate spariscono dal menu e sono bloccate anche lato server.</p>
              <SectionPicker value={permSections} onChange={setPermSections} testPrefix="perm" />
              <DeleteToggle checked={permDelete} onChange={setPermDelete} testId="perm-can-delete" />
              <DialogFooter>
                <Button variant="outline" onClick={() => setPermUser(null)}>Annulla</Button>
                <Button data-testid="save-permissions-button" onClick={savePerm} disabled={savingPerm} className="bg-accent hover:bg-accent/90 text-accent-foreground">Salva</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Nuovo utente</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs uppercase tracking-wider">Nome</Label><Input data-testid="user-name-input" className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div><Label className="text-xs uppercase tracking-wider">Email</Label><Input data-testid="user-email-input" type="email" className="mt-1.5" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><Label className="text-xs uppercase tracking-wider">Password</Label><Input data-testid="user-password-input" type="password" className="mt-1.5" value={form.password} onChange={(e) => set("password", e.target.value)} /></div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Receptionist (staff)</SelectItem>
                  <SelectItem value="admin">Direttore (admin)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "staff" && (
              <div>
                <Label className="text-xs uppercase tracking-wider">Sezioni visibili</Label>
                <div className="mt-1.5"><SectionPicker value={form.sections} onChange={(v) => set("sections", v)} testPrefix="new-perm" /></div>
                <div className="mt-3"><DeleteToggle checked={form.can_delete_bookings} onChange={(v) => set("can_delete_bookings", v)} testId="new-perm-can-delete" /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-user-button" onClick={create} className="bg-accent hover:bg-accent/90 text-accent-foreground">Crea utente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
